import React, { useState, useEffect, useMemo, useRef } from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetLeavesQuery, useGetLeaveBalanceQuery } from "../../slices/leaveApiSlice";
import { useSelector } from "react-redux";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function LeaveRecords() {
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");
  // Date range state (default to last 1 week)
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" })
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };


  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // By default show all records (no date filtering). User can pick a range if needed.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Status options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Approved", label: "Approved" },
    { value: "Pending", label: "Pending" },
    { value: "Rejected", label: "Rejected" },
    { value: "Forwarded", label: "Forwarded" },
  ];

  // Leave type options for CustomSelect
  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);

  // Get user info from Redux store
  const { userInfo } = useSelector((state) => state.auth);
  const userId = userInfo?.userId || userInfo?.id || '';



  // If there's no logged-in user, don't silently skip — show helpful message
  if (!userId) {
    return (
      <div className="p-8 max-w-10xl mx-auto">
        <div className="flex items-center space-x-1 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Leave Records</h1>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h3 className="text-yellow-800 font-semibold mb-2">Not signed in</h3>
          <p className="text-yellow-700 mb-4">You need to sign in to fetch your leave records. Please log in and try again.</p>
        </div>
      </div>
    );
  }

  // Use the leave API hook to fetch data
  const {
    data: leaveData = [],
    isLoading,
    error,
    refetch
  } = useGetLeavesQuery(userId, {
    skip: !userId, // Skip query if no userId
  });


  // Fetch leave balance for current user
  const {
    data: leaveBalanceData,
    isLoading: isLoadingBalance,
    error: balanceError,
    refetch: refetchBalance
  } = useGetLeaveBalanceQuery();

  // Calculate days based on duration
  const calculateDays = (startDate, endDate, duration) => {
    if (duration === "HALF_DAY") return 0.5;
    if (duration === "FULL_DAY") return 1;

    // For multi-day, calculate actual days
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      return diffDays;
    }

    return 1;
  };

  // Process leave balance data from API - FIXED VERSION
  const processLeaveBalance = () => {
    if (!leaveBalanceData) {
      return {
        earned: { total: 0, remaining: 0, used: 0 },
        casual: { total: 0, remaining: 0, used: 0 }
      };
    }

    // Use the data directly from API with proper fallbacks
    const earnedTotal = parseFloat(leaveBalanceData.total_earned_leave_days) || 0;
    const casualTotal = parseFloat(leaveBalanceData.total_casual_leave_days) || 0;
    const earnedRemaining = parseFloat(leaveBalanceData.earned_leave_balance_remaining) || 0;
    const casualRemaining = parseFloat(leaveBalanceData.casual_leave_balance_remaining) || 0;

    // Calculate used days: Total - Remaining
    const earnedUsed = earnedTotal - earnedRemaining;
    const casualUsed = casualTotal - casualRemaining;

    return {
      earned: {
        total: Math.max(0, earnedTotal),
        remaining: Math.max(0, earnedRemaining),
        used: Math.max(0, earnedUsed)
      },
      casual: {
        total: Math.max(0, casualTotal),
        remaining: Math.max(0, casualRemaining),
        used: Math.max(0, casualUsed)
      }
    };
  };

  // Final leave balances with direct API data
  const leaveBalances = useMemo(() => {
    return processLeaveBalance();
  }, [leaveBalanceData]);


  const handleBack = () => {
    navigate("/technician/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
      case "Approved":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "REJECTED":
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "FORWARDED":
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (short) {
        // dd/mm/yyyy format for compact display
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      // Add 6 hours to the date
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch (error) {
      return dateString;
    }
  };

  const transformLeaveData = (apiData) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    // Sort the data by submitted date in descending order (newest first)
    const sortedData = [...apiData].sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.createdAt);
      const dateB = new Date(b.submittedAt || b.createdAt);
      return dateB - dateA; // Descending order
    });

    return sortedData.map(leave => {
      const days = calculateDays(leave.startDate, leave.endDate, leave.duration);
      const isSingleDay = days === 1 || leave.duration === "FULL_DAY" || leave.duration === "HALF_DAY";

      // Extract supervisor info from workflow notes if available
      const supervisorInfo = extractSupervisorInfo(leave);
      const managerInfo = extractManagerInfo(leave);

      // Determine leave type - if not CASUAL or EARN, it's Manual Leave
      const isManualLeave = leave.leaveType !== "CASUAL" && leave.leaveType !== "EARN";

      // For display in table
      const leaveTypeDisplay = isManualLeave ? "Manual Leave" :
        leave.leaveType === "CASUAL" ? "Casual Leave" : "Earned Leave";

      // For details overlay - show the actual leave type from API
      const originalLeaveType = leave.
leaveTypeLabel || "Manual Leave";

      // For manual leaves, status should be Approved by default
      const status = isManualLeave ? "APPROVED" : (leave.status || "PENDING");

      return {
        id: leave._id || leave.id,
        type: leaveTypeDisplay, // For table display
        originalLeaveType: originalLeaveType, // For details overlay - actual leave type
        isManualLeave: isManualLeave,
        reason: leave.reason || "No reason provided",
        days: leave.totalDays,
        startDate: leave.startDate,
        endDate: isSingleDay ? "" : leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: status,
        employee: leave.Email || "Unknown",
        position: "Technician",
        email: leave.technicianEmail || "",

        // Use existing timestamp fields instead of non-existent noteDate fields
        supervisorNote: leave.supervisorNote || supervisorInfo.note || "",
        supervisorName: leave.supervisorName || supervisorInfo.name || "Supervisor",
        supervisorEmail: leave.supervisorEmail || supervisorInfo.email || "",
        supervisorNoteDate: leave.forwardedAt || supervisorInfo.date || "",

        managerNote: leave.managerNote || managerInfo.note || "",
        managerName: leave.managerName || managerInfo.name || "",
        managerEmail: leave.managerEmail || managerInfo.email || "",
        managerNoteDate: leave.decisionAt || managerInfo.date || "",

        originalEndDate: leave.endDate,
        isSingleDay: isSingleDay,
        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
      };
    });
  };
  // Enhanced extractManagerInfo to get timestamp from workflow notes
  const extractManagerInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    // Find manager notes
    const managerNotes = leave.notes.filter(note =>
      note.actorRole === 'MANAGER' || note.actorRole === 'Manager'
    );

    if (managerNotes.length > 0) {
      const latestNote = managerNotes[managerNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Manager",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || ""
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  // Enhanced extractSupervisorInfo
  const extractSupervisorInfo = (leave) => {
    if (!leave.notes || !Array.isArray(leave.notes)) {
      return { note: "", name: "", email: "", date: "" };
    }

    // Find supervisor notes
    const supervisorNotes = leave.notes.filter(note =>
      note.actorRole === 'SUPERVISOR' || note.actorRole === 'Supervisor'
    );

    if (supervisorNotes.length > 0) {
      const latestNote = supervisorNotes[supervisorNotes.length - 1];
      return {
        note: latestNote.message || "",
        name: latestNote.actorName || "Supervisor",
        email: latestNote.actorEmail || latestNote.actorId || "",
        date: latestNote.timestamp || ""
      };
    }

    return { note: "", name: "", email: "", date: "" };
  };

  // Filter data based on status, leave type and date range
  // Robust date filtering: normalize dates to UTC midnight timestamps for comparison
  const toDateMidnightTs = (d) => {
    if (!d) return null;
    // If already a Date object
    if (d instanceof Date && !isNaN(d)) {
      return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    }
    // Try parsing string input
    const parsed = new Date(d);
    if (!isNaN(parsed)) {
      return Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
    // Fallback: try YYYY-MM-DD
    const parts = String(d).split("-");
    if (parts.length === 3) {
      const y = Number(parts[0]);
      const m = Number(parts[1]) - 1;
      const day = Number(parts[2]);
      if (!isNaN(y) && !isNaN(m) && !isNaN(day)) {
        return Date.UTC(y, m, day);
      }
    }
    return null;
  };

  const startTs = toDateMidnightTs(startDate);
  const endTs = toDateMidnightTs(endDate);

  const filteredData = transformLeaveData(leaveData).filter(record => {
    const statusMatch = selectedStatus === "All" || record.status.toLowerCase() === selectedStatus.toLowerCase();
    const leaveTypeMatch = selectedLeaveType === "All" || record.type === selectedLeaveType;

    // If no date filter selected, accept by default
    if (!startTs && !endTs) return statusMatch && leaveTypeMatch;

    // Compute record start/end timestamps
    const recordStartRaw = record.startDate || record.submittedDate || null;
    const recordEndRaw = record.isSingleDay ? record.startDate : (record.endDate || record.originalEndDate || null);
    const recordStartTs = toDateMidnightTs(recordStartRaw);
    const recordEndTs = toDateMidnightTs(recordEndRaw) || recordStartTs;

    // If record dates are missing, skip date filtering (treat as match)
    if (!recordStartTs || !recordEndTs) return statusMatch && leaveTypeMatch;

    let dateMatch = true;
    if (startTs && endTs) {
      dateMatch = recordStartTs >= startTs && recordEndTs <= endTs;
    } else if (startTs) {
      dateMatch = recordEndTs >= startTs; // record ends on/after start
    } else if (endTs) {
      dateMatch = recordStartTs <= endTs; // record starts on/before end
    }

    return statusMatch && leaveTypeMatch && dateMatch;
  });



  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
  };

  // Refresh data
  const handleRefresh = () => {
    refetch();
    refetchBalance();
  };

  const LeaveBalanceCard = ({ title, balance, color = "blue", isLoading = false }) => {
  const colorClasses = {
    blue: {
      text: "text-blue-600",
      bg: "bg-blue-50",
      border: "border-blue-200",
      progress: "bg-blue-500"
    },
    purple: {
      text: "text-purple-600",
      bg: "bg-purple-50",
      border: "border-purple-200",
      progress: "bg-purple-500"
    },
    gray: {
      text: "text-gray-600",
      bg: "bg-gray-50",
      border: "border-gray-200",
      progress: "bg-gray-500"
    }
  };

  const currentColor = colorClasses[color];


  if (isLoading) {
    return (
      <div className="bg-[#FAFAFA] rounded-lg p-6 animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-1/2 mb-4"></div>
        <div className="space-y-3">
          <div className="h-3 bg-gray-300 rounded"></div>
          <div className="h-3 bg-gray-300 rounded"></div>
          <div className="h-3 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#FAFAFA] rounded-lg p-4 sm:p-6`}>
      <h3 className="font-semibold text-gray-800 mb-3 sm:mb-4 text-sm sm:text-base">{title}</h3>
      <div className="space-y-3 sm:space-y-4">
        {/* Total Entitled - DIRECT FROM API */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600 text-sm sm:text-base">Total Entitled:</span>
          <span className="font-medium text-sm sm:text-base">{balance.total} days</span>
        </div>

        {/* Used Days - Calculated as Total - Remaining */}
        <div className="flex justify-between items-center">
          <span className="text-gray-600 text-sm sm:text-base">Used:</span>
          <span className="font-medium text-red-600 text-sm sm:text-base">
            - {Math.max(0, balance.total - balance.remaining)} days
          </span>
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-gray-200">
          <span className="text-gray-800 font-semibold text-sm sm:text-base">Remaining</span>
          <div className="text-right">
            <div className={`text-sm ${currentColor.text} font-bold`}>
              {balance.remaining} days
            </div>
          </div>
        </div>

        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Remaining: {balance.remaining} days</span>
            <span>Used: {Math.max(0, balance.total - balance.remaining)} days</span>
          </div>
          <div className="flex h-2 sm:h-3 rounded-full overflow-hidden border border-gray-300">
            <div
              className={`${currentColor.progress} transition-all duration-300`}
              style={{ width: `${(balance.remaining / balance.total) * 100}%` }}
            ></div>
            <div
              className="bg-gray-200 transition-all duration-300"
              style={{ width: `${((balance.total - balance.remaining) / balance.total) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8 max-w-10xl mx-auto">
        <div className="flex items-center space-x-1 mb-6">
          <button aria-label="Back" onClick={handleBack} className="hidden md:flex w-9 h-9 items-center justify-center rounded-full">
            <ChevronLeft size={24} strokeWidth={2} className="text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Leave Records</h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 max-w-10xl mx-auto">
        <div className="flex items-center space-x-1 mb-6">
          <button aria-label="Back" onClick={handleBack} className="hidden md:flex w-9 h-9 items-center justify-center bg-gray-50 border-2 border-black rounded-full">
            <ChevronLeft size={32} strokeWidth={2} className="text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">Leave Records</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-red-800 font-semibold mb-2">Error loading leave records</h3>
          <p className="text-red-600 mb-4">{error?.data?.message || "Failed to fetch leave data"}</p>
          <button
            onClick={handleRefresh}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center space-x-1">
          <button
            aria-label="Back"
            onClick={handleBack}
            className="hidden md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
          >
            <ChevronLeft size={24} strokeWidth={2} className="text-gray-700 sm:w-6 sm:h-6" />
          </button>
          <div className="flex items-center space-x-2 sm:space-x-3">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Leave Records</h1>
          </div>
        </div>
      </div>

      {/* Leave Balance Cards at Top */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <LeaveBalanceCard
          title="Earned Leave Balance"
          balance={leaveBalances.earned}
          color="blue"
          isLoading={isLoadingBalance}
        />
        <LeaveBalanceCard
          title="Casual Leave Balance"
          balance={leaveBalances.casual}
          color="purple"
          isLoading={isLoadingBalance}
        />
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex flex-col items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">Records</h2>
              <p className="text-sm text-zinc-600 mt-1">All your leave records</p>
            </div>

            <div className="w-full mt-3">
              <div className="min-w-0 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="w-full sm:w-auto min-w-0">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                  <CustomSelect
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    placeholder="All"
                    minWidth={140}
                  />
                </div>
                <div className="w-full sm:w-auto min-w-0">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Leave Type</label>
                  <CustomSelect
                    options={leaveTypeOptions}
                    value={selectedLeaveType}
                    onChange={setSelectedLeaveType}
                    placeholder="All"
                    minWidth={140}
                  />
                </div>
                {/* <div className="w-full sm:w-auto min-w-0 relative">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                  <button
                    ref={dateRangeButtonRef}
                    onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                    title={startDate && endDate ? `${formatDate(startDate)} - ${formatDate(endDate)}` : 'All dates'}
                    className="w-full sm:w-40 md:w-56 max-w-full bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 truncate text-left"
                  >
                    {startDate && endDate ? (
                      <>
                        <span className="hidden sm:inline">
                          {formatDate(startDate)} - {formatDate(endDate)}
                        </span>
                        <span className="sm:hidden">
                          {formatDate(startDate, true)} - {formatDate(endDate, true)}
                        </span>
                      </>
                    ) : (
                      "All dates"
                    )}
                  </button>
                  <DateRangePicker
                    show={showDateRangePicker}
                    onClose={() => setShowDateRangePicker(false)}
                    startDate={startDate}
                    endDate={endDate}
                    onDateRangeSelect={({ start, end }) => {
                      if (start && end) {
                        setStartDate(start);
                        setEndDate(end);
                      }
                    }}
                    buttonRef={dateRangeButtonRef}
                    allowFutureDates={true}
                  />
                </div> */}
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TYPE
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DAYS
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  SUBMITTED ON
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  FROM
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TO
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  STATUS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {filteredData.length > 0 ? (
                filteredData.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(record)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      {record.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDateTime(record.submittedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {record.isSingleDay ? formatDate(record.startDate) : formatDate(record.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-zinc-500">
                    No leave records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-3 sm:p-4">
              {/* Header with Status and Profile */}
              <div className="flex justify-between items-start mb-3 sm:mb-4">
                <div className="space-y-2 sm:space-y-3">
                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <span className={`inline-flex px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}>
                      {selectedLeave.status}
                    </span>
                  </div>

                  {/* Profile */}
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.employee.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">{selectedLeave.employee}</h3>
                      <p className="text-xs sm:text-sm text-gray-600">{selectedLeave.position}</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>
              {/* Leave Type */}
              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">{selectedLeave.originalLeaveType}</h3>
              </div>

              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">Reason:</h4>
                <p className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <p className="text-sm text-blue-600 mt-1">
                      Half Day - {selectedLeave.halfDaySession === 'MORNING' ? 'Morning' : 'Afternoon'} Session
                    </p>
                  )}
                  {selectedLeave.reason}
                </p>
              </div>

              {/* Dates and Days */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Submitted On:</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>

                {/* Show "Leave Date" for single day leaves (including half-day) */}
                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Leave Date:</span>
                    <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">From:</span>
                      <span className="text-gray-900 text-sm sm:text-base">{formatDate(selectedLeave.startDate)}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">To:</span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">Days</span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days} {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Notes Section - Different for Manual vs Regular Leaves */}
              <div className="bg-gray-50 rounded-lg">
                {/* For Manual Leaves - Show message instead of notes */}
                {selectedLeave.isManualLeave ? (
                  <div className="p-4 text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-center mb-2">
                      </div>
                      <h4 className="font-semibold text-green-800 text-sm sm:text-base mb-2">
                        Manual Leave Approved
                      </h4>
                      <p className="text-green-700 text-sm">
                        Manual leaves are automatically approved and do not require supervisor or manager review.
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Supervisor Note - Only show for non-manual leaves */}
                    {selectedLeave.supervisorNote && (
                      <div className="p-2 sm:p-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                              {selectedLeave.supervisorName?.charAt(0) || 'S'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              {selectedLeave.supervisorEmail || "Supervisor"}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600">Supervisor</p>
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-gray-700 text-sm sm:text-base">{selectedLeave.supervisorNote}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Note added on: {formatDateTime(selectedLeave.supervisorNoteDate)}
                          </p>
                        </div>

                        {/* Border only if Manager Note exists */}
                        {selectedLeave.managerNote && (
                          <div className="pb-2 mb-2">
                            <div className="border-b border-gray-300 pb-2 mt-2 mb-1"></div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manager Note - Only show for non-manual leaves */}
                    {selectedLeave.managerNote && (
                      <div className="p-2 sm:p-3">
                        <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <span className="text-green-600 font-semibold text-xs sm:text-sm">
                              {selectedLeave.managerName?.charAt(0) || 'M'}
                            </span>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                              {selectedLeave.managerEmail}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600">Manager</p>
                          </div>
                        </div>
                        <div className="p-1">
                          <p className="text-gray-700 text-sm sm:text-base">{selectedLeave.managerNote}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            Note added on: {formatDateTime(selectedLeave.managerNoteDate)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Show message if no notes exist for non-manual leaves */}
                    {!selectedLeave.supervisorNote && !selectedLeave.managerNote && (
                      <div className="p-4 text-center">
                        <p className="text-gray-600 text-sm">No review notes available for this leave request.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}